/**
 * Service layer — Key Dates.
 *
 * Owns the project_key_dates / project_key_date_milestones tables (Supabase) behind the
 * Clerk-authed /api/key-dates endpoint. See database/262_project_key_dates.sql and
 * docs/KEY_DATES.md. A key date belongs to a PROJECT and can be linked to any number of that
 * project's milestones — the same row shows under every linked milestone, so editing it from
 * one place updates all of them at once.
 */

/** Mirrors services/studioCalendar.js's token() — waits briefly for window.__clerkGetToken. */
async function token() {
  for (let i = 0; i < 25; i++) {
    if (typeof window !== 'undefined' && typeof window.__clerkGetToken === 'function') {
      try { const t = await window.__clerkGetToken(); if (t) return t } catch { /* retry */ }
    }
    await new Promise(r => setTimeout(r, 150))
  }
  return null
}

async function post(payload) {
  const headers = { 'Content-Type': 'application/json' }
  const t = await token(); if (t) headers['Authorization'] = 'Bearer ' + t
  const res = await fetch('/api/key-dates', { method: 'POST', headers, body: JSON.stringify(payload) })
  const data = await res.json().catch(() => ({ error: 'Bad response from /api/key-dates' }))
  if (!res.ok || data.error) throw new Error(data.error || ('Request failed (' + res.status + ')'))
  return data
}

/** All key dates for a project, each carrying { milestoneIds }. Used by both the milestone
 *  Key Dates section (filters by msId membership) and the Gantt (diamonds). */
export async function getProjectKeyDates(projectId) {
  if (!projectId) return []
  return (await post({ action: 'list', projectId })).keyDates || []
}

export async function createKeyDate(projectId, { label, date, notes, milestoneIds, memberId, memberName }) {
  return (await post({ action: 'create', projectId, label, date, notes, milestoneIds, memberId, memberName })).keyDate
}

export async function updateKeyDate(id, { label, date, notes, memberId, memberName }) {
  return (await post({ action: 'update', id, label, date, notes, memberId, memberName })).keyDate
}

export async function linkKeyDateToMilestones(id, milestoneIds) {
  return (await post({ action: 'link', id, milestoneIds })).keyDate
}

/** Unlink from one milestone — leaves the key date itself (and its other links) intact. */
export async function unlinkKeyDateFromMilestone(id, milestoneId) {
  return await post({ action: 'unlink', id, milestoneId })
}

/** Delete the key date entirely — removes it (and every link) from every milestone showing it. */
export async function deleteKeyDate(id) {
  return (await post({ action: 'delete', id })).ok
}

/** Best-effort hook called after every milestone create — no-ops server-side unless `typeId` is
 *  Public Hearing Preparation / Public Hearing. See services/milestones.js#createMilestone. */
export async function ensureHearingDate(projectId, milestoneId, typeId, subtitle) {
  return (await post({ action: 'ensureHearingDate', projectId, milestoneId, typeId, subtitle })).keyDate || null
}
